
import { useContext, computed, onMounted, watch } from '@nuxtjs/composition-api'
import { storeToRefs } from 'pinia'
import useTlmCart from '@/composable/useTlmCart';
import { PRODUCT_TYPE } from '@/constants/ProductType';
import { useReceipt } from '~/composable/useReceipt';
import useUtilities from '~/composable/useUtilities';
import useCheckout from '@/composable/checkout/useCheckout';
import { CUSTOMER_SUBSCRIPTION_TYPE, CUSTOMER_TYPE, SIM_CARD_TYPE } from '@/constants/CheckoutFlow';
import usePlaymix from '@/composable/usePlaymix';
import useSelectorSteps from '~/composable/useSelectorSteps';
import useCartQueries from '~/composable/useCartQueries';
import useProductType from '~/composable/useProductType';
import useUserInQuarantine from '@/composable/checkout/validations/useUserInQurantine';
import useCheckoutInfo from '~/composable/useCheckoutInfo';
import { voucherDebug, voucherDebugWarn } from '~/composable/voucher/useVoucherDebug';
import useMonthlyPrice from '~/composable/useMonthlyPrice';
import { useResolveVoucherDevicePrice } from '~/composable/voucher/useResolveVoucherDevicePrice';
import { cartHasVoucherDiscount, getVoucherDiscountFromCart, isPercentageVoucherDiscount } from '~/composable/voucher/resolveDevicePriceForCart';
import { useAppliedDiscountCodeStore } from '~/stores/voucher/useAppliedDiscountCodeStore';
import useMultiplePlayMixServices from '~/composable/useMultiplePlayMixServices';
import useProductConfigurationCatalog from '~/composable/useProductConfigurationCatalog';
import { subscriptionHasPlayServices } from '~/composable/useSubscriptionPlayServices';
import { buildPartnerCodeSet, parsePlayPoList, parsePlayPoListParts, resolvePlayMixIconCodes } from '~/utils/playPoList';

export default {
  name: 'TlmCheckoutCartContent',
  props: {
    isReceipt: {
      type: Boolean,
      default: false
    }
  },
  setup (props) {
    // const poList = ref()
    const { formatPrice, formatPriceWithDecimals } = useUtilities()
    const { app } = useContext()
    const useCartContent = props.isReceipt ? useReceipt : useTlmCart
    const {
      cart,
      isCartEmpty,
      isLoading,
      hasInstallment,
      getItemAttributeFromCartItem,
      isNewNumber,
      isChangeSubscription,
      isNumberPortingSelected,
      isNewNumberSelected,
      subscriptionList,
      isPayGSubscription,
      keepSubCustInDiscountedPeriod,
      containsKeepSubscription,
      isEsimOnlyDevice
    } = useCartContent(app)
    const {
      cartOnlyContainsSubscription, playMixProducts, isAnyItemAssociatedWithMobileVoiceSubscription,
      isAnyItemAssociatedWithMobileBroadBand, isAnyItemAssociatedWithSubscription
    } = useCartQueries(app, cart, { skipAutoLoad: props.isReceipt });
    const { isUserInQuarantine, fullPriceMOProratedPrice, fullPriceSubscriptionName } = useUserInQuarantine(app)
    const { isMobileBroadBandSubscription, isSubscription } = useProductType();
    const { getTotalMonthlyPrice: calculateTotalMonthlyPrice } = useMonthlyPrice()
    const { resolveFromCartItem, getPdpProductBundlePrice } = useResolveVoucherDevicePrice()
    const resolveDeviceLineItemPrice = (cartItem) => {
      return resolveFromCartItem(cartItem, cart.value)
    }
    const appliedDiscountCodeStore = useAppliedDiscountCodeStore()
    const { hasAppliedDiscountCode, totalDiscountCodeDiscount } = storeToRefs(appliedDiscountCodeStore)

    const { state } = useCheckout(app)
    const subscription = state.subscription

    const { getSubscriptionName } = useUtilities()
    const { getPlayMixServices, data } = usePlaymix('playmix-4-for-internet', app.$hubble)
    const { getPlayPromotionalContent, getImage } = useMultiplePlayMixServices(app)
    const { isLoanModem: isOnLoan } = useSelectorSteps(app)
    const { loadProductConfigurationCatalog, catalogData, planMap, buildTilkobDisplayItems } = useProductConfigurationCatalog()

    const hasPlayServicesForCartItem = (cartItem) => {
      return subscriptionHasPlayServices(cartItem?.cartProduct?.additionalBillingInformation)
    }

    const getImageUrl = (cartItem) => {
      return cartItem?.cartProduct?.productContent?.productImages[0]?.imageUrl || null
    }

    const subscriptionTypeMenuSelected = computed(() => {
      return subscription?.menu?.value
    })

    const playPoListParseContext = () => ({
      planMap: planMap.value,
      partnerCodes: buildPartnerCodeSet(catalogData.value)
    })

    const resolvePlayIconUrl = (code) => {
      const direct = getImage(code)
      if (direct) {
        return direct
      }
      // Plan codes in PlayPoList (e.g. SkyShowtimeStandardPlusPlan) → partner product for Contentful vasIds
      const partnerProduct = planMap.value?.get(code)?.partnerProduct
      return partnerProduct ? getImage(partnerProduct) : undefined
    }

    const getPlayIconCodesForCartItem = (cartItem) => {
      const playPoListParts = parsePlayPoListParts(getItemAttributeFromCartItem(cartItem, 'PlayPoList'))
      if (playPoListParts.length) {
        return playPoListParts
      }
      const playServices = cartItem?.cartProduct?.additionalBillingInformation?.playServices
      if (!Array.isArray(playServices)) {
        return []
      }
      return playServices.map(code => (typeof code === 'string' ? code.trim() : '')).filter(Boolean)
    }

    const selectedPlayMixPO = (cartItem) => {
      const parts = getPlayIconCodesForCartItem(cartItem)
      const codes = resolvePlayMixIconCodes(parts, resolvePlayIconUrl)
      if (!codes?.length) {
        return null
      }
      // Only keep codes that resolve to a Contentful image (play MOs never use Metranet iconLink)
      const withIcons = codes.filter(code => code !== null && resolvePlayIconUrl(code))
      return withIcons.length ? withIcons : null
    }

    const parsePartnerAddonsFromCartItem = (cartItem) => {
      const raw = getItemAttributeFromCartItem(cartItem, 'PartnerAddons')
      if (typeof raw !== 'string' || raw === '') {
        return []
      }
      return raw.split(',').map(s => s.trim()).filter(Boolean)
    }

    const getMixCodesForCartItem = (cartItem) => {
      const productOfferings = getItemAttributeFromCartItem(cartItem, 'PlayPoList')
      const { partners, plans } = parsePlayPoList(productOfferings, playPoListParseContext())
      return plans.length > 0 ? plans : (partners || [])
    }

    const getTilkobItemsForCartItem = (cartItem) => {
      const mixCodes = getMixCodesForCartItem(cartItem)
      const addonCodes = parsePartnerAddonsFromCartItem(cartItem)
      return buildTilkobDisplayItems(mixCodes, addonCodes)
    }

    const hasTilkobItemsForCartItem = (cartItem) => getTilkobItemsForCartItem(cartItem)?.length > 0

    //
    const getPlanDisplayName = (plan) => {
      if (!plan) {
        return ''
      }
      // Prefer Contentful/backend display fields when present; fall back to existing product label.
      return plan.displayName
    }

    const getAddonDisplayName = (addon) => {
      if (!addon) {
        return ''
      }
      return addon.displayName || addon.shortName || addon.code
    }

    const getValidCartItems = (cartItemGroup) => {
      return (cartItemGroup?.cartItems || []).filter((item) => item?.cartProduct)
    }

    const getValidCartItemCount = (cartItemGroup) => {
      return getValidCartItems(cartItemGroup).length
    }

    const loadPlayMixCartAssets = async (mixSku) => {
      await getPlayPromotionalContent()
      await loadProductConfigurationCatalog()
      if (mixSku) {
        try {
          await getPlayMixServices(mixSku)
        } catch (error) {
          console.warn('CartContent: getPlayMixServices failed', error)
        }
      }
    }

    onMounted(() => {
      loadPlayMixCartAssets(playMixProducts.value?.sku)
    })

    watch(playMixProducts, (mixItem) => {
      if (mixItem?.sku) {
        loadPlayMixCartAssets(mixItem.sku)
      }
    })

    const playMixIcon = (poId) => {
      return resolvePlayIconUrl(poId)
    }

    const isLoanModem = (cartProduct) => {
      return cartProduct.productTypeKey === 'mobilebroadbandhardware' && isOnLoan(cartProduct)
    }

    const isAssociatedWithMBBAndReceipt = computed(() => isAnyItemAssociatedWithMobileBroadBand && props.isReceipt)
    // eslint-disable-next-line complexity
    const showChosenNumber = cartItem => {
      // show when user chooses new number but not for MBB flow
      const isAssociatedWithMobileVoiceSubscriptionAndNewNumber = isAnyItemAssociatedWithMobileVoiceSubscription.value && isNewNumber(cartItem);
      const isNewNumberOrReceipt = isNewNumber(cartItem) || props.isReceipt;
      const isNewNumberMenuSelected = subscriptionTypeMenuSelected.value === CUSTOMER_SUBSCRIPTION_TYPE.NEW_NUMBER


      if (isAssociatedWithMobileVoiceSubscriptionAndNewNumber && isNewNumberMenuSelected) {
        return true
      }

      if (isNewNumberOrReceipt && !isAnyItemAssociatedWithMobileBroadBand) {
        return true
      }

      return isAssociatedWithMBBAndReceipt.value
    }
    const numberPresentInReceipt = (cartItem) => {
      if (props.isReceipt) {
        return getItemAttributeFromCartItem(cartItem, 'PhoneNumber') !== null
      }
      return true
    }

    const getProductWithSubscriptionPrice = (cartItem) => {
      return resolveDeviceLineItemPrice(cartItem)
    }

    const hasDeviceVoucherDiscount = computed(() => {
      if (cartHasVoucherDiscount(cart.value)) {
        return true
      }
      return hasAppliedDiscountCode.value
    })

    const usesPercentageVoucherDiscount = computed(() => {
      if (!hasDeviceVoucherDiscount.value) {
        return false
      }
      if (isPercentageVoucherDiscount(cart.value)) {
        return true
      }
      return isPercentageVoucherDiscount(appliedDiscountCodeStore.appliedDiscountCodes)
    })

    const formatDisplayPrice = (nr) => {
      if (usesPercentageVoucherDiscount.value) {
        return formatPriceWithDecimals(nr)
      }
      return formatPrice(nr)
    }

    const deviceVoucherDiscountAmount = computed(() => {
      const cartDiscount = getVoucherDiscountFromCart(cart.value)
      if (cartDiscount > 0) {
        return cartDiscount
      }
      return totalDiscountCodeDiscount.value || 0
    })

    const isDeviceOrAccessoryCartItem = (cartItem) => {
      const productTypeKey = cartItem?.cartProduct?.productTypeKey
      return productTypeKey === PRODUCT_TYPE.DEVICE || productTypeKey === PRODUCT_TYPE.ACCESSORY
    }

    const showCheckoutDeviceFinalPrice = (cartItem) => {
      if (isLoanModem(cartItem.cartProduct)) {
        return false
      }
      return (isDeviceOrAccessoryCartItem(cartItem) && getCheckoutDeviceDiscount(cartItem) > 0) || hasDeviceVoucherDiscount.value
    }

    const getPdpPriceField = (cartItem, field, fallback) => {
      const pdpValue = getPdpProductBundlePrice(cartItem)?.[field]
      if (pdpValue !== undefined && pdpValue !== null) {
        return pdpValue
      }
      return fallback
    }

    const getCheckoutDeviceBasePrice = (cartItem) => {
      return getPdpPriceField(
        cartItem,
        'basePrice',
        cartItem.cartProduct.selectedPrice.basePrice
      )
    }

    const getCheckoutDeviceDiscount = (cartItem) => {
      return getPdpPriceField(
        cartItem,
        'discount',
        cartItem.cartProduct.selectedPrice.discount
      )
    }

    const shouldUseResolvedDevicePrice = (storedPrice, resolvedPrice) => {
      if (storedPrice === null || storedPrice === undefined) {
        return true
      }
      return Math.abs(storedPrice - resolvedPrice) > 1
    }

    const resolveStoredOrAdjustedDevicePrice = (cartItem, storedPrice) => {
      if (!isDeviceOrAccessoryCartItem(cartItem)) {
        return storedPrice ?? 0
      }
      const resolvedPrice = resolveDeviceLineItemPrice(cartItem)
      if (shouldUseResolvedDevicePrice(storedPrice, resolvedPrice)) {
        return resolvedPrice
      }
      return storedPrice
    }

    const getChangeSubscriptionPayNowPrice = (cartItem, cartItemGroup) => {
      return resolveStoredOrAdjustedDevicePrice(
        cartItem,
        cartItemGroup?.cartItemGroupPrices?.changeSubscriptionPrice
      )
    }

    const getDefaultPayNowPrice = (cartItem, cartItemGroup) => {
      return resolveStoredOrAdjustedDevicePrice(
        cartItem,
        cartItemGroup?.cartItemGroupPrices?.defaultPrice
      )
    }

    const getSubscriptionCartItem = (cartItemGroup) => {
      return cartItemGroup?.cartItems?.find(item =>
        isSubscription(item.cartProduct?.productTypeKey)
      )
    }

    const getDiscountedMonths = (unsubscribableAfterDays) => {
      if (unsubscribableAfterDays <= 0) {
        return 1
      }
      return Math.floor(unsubscribableAfterDays / 30)
    }

    const hasValidDiscountPeriod = (billingInfo) => {
      if (!billingInfo?.hasDiscountPeriod || !billingInfo?.unsubscribableAfterDays) {
        return false
      }
      return billingInfo?.recurringDiscountPayment?.chargeValue != null &&
        billingInfo?.recurringPayment?.chargeValue != null
    }

    const calculateDiscountedBindingPeriodPayment = (billingInfo, bindingPeriodMonths) => {
      const discountedMonths = getDiscountedMonths(billingInfo.unsubscribableAfterDays)
      const discountedPrice = billingInfo.recurringDiscountPayment.chargeValue
      const normalPrice = billingInfo.recurringPayment.chargeValue
      return (discountedPrice * discountedMonths) + (normalPrice * (bindingPeriodMonths - discountedMonths))
    }

    const calculateBindingPeriodPayment = (subscriptionCartItem) => {
      const billingInfo = subscriptionCartItem?.cartProduct?.additionalBillingInformation
      const bindingPeriodMonths = billingInfo?.bindingPeriod || 0
      if (hasValidDiscountPeriod(billingInfo)) {
        return calculateDiscountedBindingPeriodPayment(billingInfo, bindingPeriodMonths)
      }
      const subscriptionPrice = subscriptionCartItem?.cartProduct?.selectedPrice?.lineItemPrice || 0
      return subscriptionPrice * bindingPeriodMonths
    }

    const getSignUpFeeWithBinding = (subscriptionCartItem) => {
      return subscriptionCartItem?.cartProduct?.additionalBillingInformation?.signUpFeeWithBinding || 0
    }

    const calculateMinimumPriceFromComponents = (productWithSubscriptionPrice, bindingPeriodPayment, signUpFeeWithBinding) => {
      return productWithSubscriptionPrice + bindingPeriodPayment + signUpFeeWithBinding
    }

    const logCalculatedMinimumPrice = (productWithSubscriptionPrice, subscriptionPrice, bindingPeriodMonths, bindingPeriodPayment, signUpFeeWithBinding, calculatedMinimumPrice, storedMinimumPrice) => {
      voucherDebug('CartContent - getMinimumPrice calculated from cart:', {
        productWithSubscriptionPrice,
        subscriptionPrice,
        bindingPeriodMonths,
        bindingPeriodPayment,
        signUpFeeWithBinding,
        calculatedMinimumPrice,
        storedMinimumPrice
      })
    }

    const logStoredMinimumPrice = (storedMinimumPrice, productWithSubscriptionPrice, subscriptionPrice, bindingPeriodMonths) => {
      voucherDebugWarn('CartContent - getMinimumPrice using stored value (may be incorrect):', {
        storedMinimumPrice,
        productWithSubscriptionPrice,
        subscriptionPrice,
        bindingPeriodMonths
      })
    }

    const logMinimumPriceError = (productWithSubscriptionPrice, subscriptionPrice, bindingPeriodMonths, signUpFeeWithBinding) => {
      console.error('CartContent - getMinimumPrice: No valid values found', {
        productWithSubscriptionPrice,
        subscriptionPrice,
        bindingPeriodMonths,
        signUpFeeWithBinding
      })
    }

    const getSubscriptionDetails = (subscriptionCartItem) => {
      const subscriptionPrice = subscriptionCartItem?.cartProduct?.selectedPrice?.lineItemPrice || 0
      const bindingPeriodMonths = subscriptionCartItem?.cartProduct?.additionalBillingInformation?.bindingPeriod || 0
      return { subscriptionPrice, bindingPeriodMonths }
    }

    const tryUseCalculatedMinimumPrice = (calculatedMinimumPrice, productWithSubscriptionPrice, subscriptionCartItem, bindingPeriodPayment, signUpFeeWithBinding, cartItem) => {
      if (calculatedMinimumPrice <= 0) {
        return null
      }
      const { subscriptionPrice, bindingPeriodMonths } = getSubscriptionDetails(subscriptionCartItem)
      const storedMinimumPrice = cartItem?.cartProduct?.selectedPrice?.minimumPrice
      logCalculatedMinimumPrice(productWithSubscriptionPrice, subscriptionPrice, bindingPeriodMonths, bindingPeriodPayment, signUpFeeWithBinding, calculatedMinimumPrice, storedMinimumPrice)
      return calculatedMinimumPrice
    }

    const tryUseStoredMinimumPrice = (cartItem, productWithSubscriptionPrice, subscriptionCartItem) => {
      const storedMinimumPrice = cartItem?.cartProduct?.selectedPrice?.minimumPrice || 0
      if (storedMinimumPrice <= 0) {
        return null
      }
      const { subscriptionPrice, bindingPeriodMonths } = getSubscriptionDetails(subscriptionCartItem)
      logStoredMinimumPrice(storedMinimumPrice, productWithSubscriptionPrice, subscriptionPrice, bindingPeriodMonths)
      return storedMinimumPrice
    }

    const getMinimumPrice = (cartItem, cartItemGroup) => {
      // Calculate minimum price from cart values to match PDP calculation
      // Minimum price = productWithSubscriptionPrice + bindingPeriodPayment + signUpFeeWithBinding
      // This ensures checkout matches PDP (cart contains latest prices from PDP, including any voucher adjustments)

      const productWithSubscriptionPrice = getProductWithSubscriptionPrice(cartItem)
      const subscriptionCartItem = getSubscriptionCartItem(cartItemGroup)
      const bindingPeriodPayment = calculateBindingPeriodPayment(subscriptionCartItem)
      const signUpFeeWithBinding = getSignUpFeeWithBinding(subscriptionCartItem)
      const calculatedMinimumPrice = calculateMinimumPriceFromComponents(productWithSubscriptionPrice, bindingPeriodPayment, signUpFeeWithBinding)

      const result = tryUseCalculatedMinimumPrice(calculatedMinimumPrice, productWithSubscriptionPrice, subscriptionCartItem, bindingPeriodPayment, signUpFeeWithBinding, cartItem)
      if (result !== null) {
        return result
      }

      const storedResult = tryUseStoredMinimumPrice(cartItem, productWithSubscriptionPrice, subscriptionCartItem)
      if (storedResult !== null) {
        return storedResult
      }

      const { subscriptionPrice, bindingPeriodMonths } = getSubscriptionDetails(subscriptionCartItem)
      logMinimumPriceError(productWithSubscriptionPrice, subscriptionPrice, bindingPeriodMonths, signUpFeeWithBinding)
      return 0
    }

    const getTotalMonthlyPrice = (cartItem, cartItemGroup) => {
      const totalMonthlyPrice = calculateTotalMonthlyPrice(cartItem, cartItemGroup, isSubscription)
      if (totalMonthlyPrice <= 0) {
        voucherDebugWarn('CartContent - getTotalMonthlyPrice: fallback to 0', {
          hasInstallment: hasInstallment(cartItem),
          productTypeKey: cartItem?.cartProduct?.productTypeKey
        })
      } else {
        voucherDebug('CartContent - getTotalMonthlyPrice calculated from shared helper:', {
          totalMonthlyPrice,
          productTypeKey: cartItem?.cartProduct?.productTypeKey
        })
      }
      return totalMonthlyPrice
    }

    const { getCustomerType } = useCheckoutInfo(app)
    const customerType = getCustomerType()
    const isBusinessCustomer = customerType === CUSTOMER_TYPE.BUSINESS

    return {
      hasPlayServicesForCartItem,
      fullPriceSubscriptionName,
      fullPriceMOProratedPrice,
      keepSubCustInDiscountedPeriod,
      numberPresentInReceipt,
      isPayGSubscription,
      isUserInQuarantine,
      isAssociatedWithMBBAndReceipt,
      isAnyItemAssociatedWithMobileBroadBand,
      isMobileBroadBandSubscription,
      getValidCartItems,
      getValidCartItemCount,
      showChosenNumber,
      isLoanModem,
      isSubscriptionOnly: cartOnlyContainsSubscription,
      playMixIcon,
      selectedPlayMixPO,
      getTilkobItemsForCartItem,
      hasTilkobItemsForCartItem,
      getPlanDisplayName,
      getAddonDisplayName,
      subscriptionList,
      isAnyItemAssociatedWithMobileVoiceSubscription,
      CUSTOMER_SUBSCRIPTION_TYPE,
      formatPrice,
      formatDisplayPrice,
      subscription,
      subscriptionTypeMenuSelected,
      isNewNumber,
      isChangeSubscription,
      isNumberPortingSelected,
      isNewNumberSelected,
      getItemAttributeFromCartItem,
      getImageUrl,
      hasInstallment,
      isSubscription,
      isLoading,
      cart,
      isCartEmpty,
      PRODUCT_TYPE,
      isAnyItemAssociatedWithSubscription,
      getSubscriptionName,
      data,
      isEsimOnlyDevice,
      isBusinessCustomer,
      getTotalMonthlyPrice,
      getMinimumPrice,
      resolveDeviceLineItemPrice,
      hasDeviceVoucherDiscount,
      deviceVoucherDiscountAmount,
      showCheckoutDeviceFinalPrice,
      getCheckoutDeviceBasePrice,
      getCheckoutDeviceDiscount,
      getChangeSubscriptionPayNowPrice,
      containsKeepSubscription,
      getDefaultPayNowPrice
    }
  },
  computed: {
    SIM_CARD_TYPE() {
      return SIM_CARD_TYPE
    }
  }
}
